import React, { useContext, useEffect, useState } from 'react';
import { Context } from '../../../App';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import InputAdornment from '@mui/material/InputAdornment';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CircularProgress from '@mui/material/CircularProgress';
import SeoHelmet from '../../common/SeoHelmet';
import { useCalculationContext, CalculationContextProvider } from '../../context/calculationContext';
import useLoadSavedCalculation from '../useLoadSavedCalculation';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import MuiSnackbar from '../../common/MuiSnackbar';
import Sidebar from '../../sidebar/Sidebar';
import DecisionModal from '../../modals/DecisionModal';
import PricingInfoModal from '../../modals/PricingInfoModal';
import OutputLayer from './OutputLayer';
import ButtonContainer from '../../common/buttonContainerForLayeredCalculations';
import LabelContainer from './LabelContainer';
import Button from '@mui/material/Button';
import round from '../../../helpers/round';
import { useCheckMissingParamsForLayeredCalculations } from '../../../hooks/useCheckMissingParamsForLayeredCalculations';

const Liquefaction = () => {
  const { user } = useContext(Context);
  const {
    MAX_NAME_LENGTH,
    isLoadingCalculate,
    isLoadingLoadAll,
    isLoadingLoad,
    isLoadingSave,
    result,
    setResult,
    resultsArray,
    setResultsArray,
    resultProps,
    calculations,
    setShowOutput,
    showOutput,
    calculateButtonDisabled,
    setCalculateButtonDisabled,
    saveButtonDisabled,
    setSaveButtonDisabled,
    errorMessage,
    displaySidebar,
    setDisplaySidebar,
    decisionModalIsOpen,
    decision,
    setDecision,
    handleSidebarDisplay,
    setDecisionModalIsOpenToFalse,
    showMuiSnack,
    open,
    setOpen,
    alertMessage,
    severity,
    onChangeWarningForPositiveDecimals,
    calculate,
    loadOneCalculation,
    loadAllCalculations,
    update,
    checkCalculationNameforSaving,
    checkCalculationNameforSavingAs,
    deleteCalculations,
    calculationCountReached,
    saveCountReached,
  } = useCalculationContext();

  //SET STATES FOR INPUT VARIABLES
  const [name, setName] = useState('');
  const [gamma, setGamma] = useState('');
  const [gwt, setGwt] = useState('');
  const [hammerType, setHammerType] = useState(0);
  const [samplerType, setSamplerType] = useState(0);
  const [boreholeDiameter, setBoreholeDiameter] = useState(0);
  const [mw, setMw] = useState('');
  const [sds, setSds] = useState('');
  const [layersFront, setLayersFront] = useState([]);
  const [expandedLayerId, setExpandedLayerId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showPdfWarningModal, setShowPdfWarningModal] = useState(false);

  // SET THE LOADED INPUTS
  const setInputs = (inputs) => {
    setName(inputs.name);
    setGamma(inputs.gamma);
    setGwt(inputs.gwt);
    setHammerType(inputs.hammerType);
    setSamplerType(inputs.samplerType);
    setBoreholeDiameter(inputs.boreholeDiameter);
    setMw(inputs.mw);
    setSds(inputs.sds);
    setLayersFront(inputs.layersFront);
  };

  //INPUTS FOR CALCULATION
  const calculationInput = {
    name,
    gamma,
    gwt,
    hammerType,
    samplerType,
    boreholeDiameter,
    mw,
    sds,
    layersFront,
  };

  //ADD LAYER
  const addLayer = (e) => {
    e.preventDefault();
    const layer = {
      id: layersFront.length + 1,
      z: (layersFront.length + 1) * 1.5,
      SPT: '',
      uscs: 0,
      fc: '',
    };

    const newLayersFront = layersFront.slice();
    newLayersFront.push(layer);
    setLayersFront(newLayersFront);

    const newResultsArray = resultsArray.slice();
    newResultsArray.push([]);
    setResultsArray(newResultsArray);
  };

  //REMOVE LAYER
  const removeLayer = (e) => {
    e.preventDefault();
    const newLayersFront = layersFront.slice();
    newLayersFront.pop();
    setLayersFront(newLayersFront);
    resultsArray.pop();
  };

  // MANAGE SAVE & CALCULATE BUTTONS DISABLED/ENABLED STATES
  useEffect(() => {
    setCalculateButtonDisabled(false);
    setSaveButtonDisabled(false);

    if (
      (calculationInput.name || calculationInput.name === 0) &&
      (calculationInput.gamma || calculationInput.gamma === 0) &&
      (calculationInput.gwt || calculationInput.gwt === 0) &&
      (calculationInput.hammerType || calculationInput.hammerType === 0) &&
      (calculationInput.samplerType || calculationInput.samplerType === 0) &&
      (calculationInput.boreholeDiameter || calculationInput.boreholeDiameter === 0) &&
      (calculationInput.mw || calculationInput.mw === 0) &&
      (calculationInput.sds || calculationInput.sds === 0)
    ) {
      setCalculateButtonDisabled(false);
      setSaveButtonDisabled(false);
    } else {
      setCalculateButtonDisabled(true);
      setSaveButtonDisabled(true);
    }
  }, [
    setCalculateButtonDisabled,
    setSaveButtonDisabled,
    calculationInput.name,
    calculationInput.gamma,
    calculationInput.gwt,
    calculationInput.hammerType,
    calculationInput.samplerType,
    calculationInput.boreholeDiameter,
    calculationInput.mw,
    calculationInput.sds,
  ]);

  //check layer mising params and handle calculate button disabled/enabled status
  const doLayersHaveMissingParams = useCheckMissingParamsForLayeredCalculations(layersFront);
  useEffect(() => {
    setCalculateButtonDisabled(doLayersHaveMissingParams);
    setSaveButtonDisabled(doLayersHaveMissingParams);
  }, [doLayersHaveMissingParams, layersFront, setCalculateButtonDisabled, setSaveButtonDisabled]);

  // Do not render output if any input parameter except name changes
  useEffect(() => {
    setResultsArray([]);
    setResult({});
  }, [
    setResultsArray,
    setResult,
    calculationInput.gamma,
    calculationInput.gwt,
    calculationInput.hammerType,
    calculationInput.samplerType,
    calculationInput.boreholeDiameter,
    calculationInput.mw,
    calculationInput.sds,
  ]);

  //CLEAR INPUTS AND CLEAR OUTPUT
  const createNewCalculation = () => {
    setName('');
    setGamma('');
    setGwt('');
    setHammerType(0);
    setSamplerType(0);
    setBoreholeDiameter(0);
    setMw('');
    setSds('');
    setLayersFront([]);
    setShowOutput(false);
  };

  //API URLS
  const URL = {
    CALCULATE: '/api/v1/calculations/liquefaction',
    LOADONE: `/api/v1/calculations/liquefaction/loadOne/`,
    LOADALL: '/api/v1/calculations/liquefaction/loadAll',
    UPDATE: `/api/v1/calculations/liquefaction/update/`,
    SAVE: '/api/v1/calculations/liquefaction/save',
    CHECKUNIQUENAME: `/api/v1/calculations/liquefaction/checkUniqueName/`,
    DELETE: '/api/v1/calculations/liquefaction/delete',
  };
  useLoadSavedCalculation({ URL, loadOneCalculation, calculate, setInputs, showMuiSnack });

  const handleChangeName = (e) => {
    if (e.target.value.length >= MAX_NAME_LENGTH) {
      showMuiSnack(`Calculation name can be max ${MAX_NAME_LENGTH} characters.`, 'warning');
    }
    setName(e.target.value);
  };

  const handleExportPdf = async () => {
    if (isExporting) return;
    if (user.role === 'basic' || user.role === 'standard') {
      setShowPdfWarningModal(true);
      return;
    }
    setIsExporting(true);
    try {
      const payload = {
        ...calculationInput,
      };

      const res = await fetch('/api/v1/pdf/liquefaction', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error('PDF generation failed');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'geocalc-liquefaction-report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setIsExporting(false);
    }
  };

  const [isNonliqLayerExists, setIsNonliqLayerExists] = useState(false);

  useEffect(() => {
    const filteredArray = resultProps.filter((layer) => {
      return layer.fs === -1;
    });
    if (filteredArray.length > 0) {
      setIsNonliqLayerExists(true);
    } else {
      setIsNonliqLayerExists(false);
    }
  }, [resultProps]);

  // RETURN INPUT AND OUTPUT JSX
  return (
    <>
      <SeoHelmet
        title="GeoCalc | Soil Liquefaction (SPT-based)"
        description="Calculate soil liquefaction safety factors based on SPT values for a given borehole log."
        url="https://www.geocalc.app/calculations/liquefaction-tbdy"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="tool"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'Soil Liquefaction', url: 'https://www.geocalc.app/calculations/liquefaction-tbdy' },
        ]}
      />
      <Header />
      <div className="container">
        <MuiSnackbar open={open} setOpen={setOpen} severity={severity} alertMessage={alertMessage} />
        {displaySidebar ? (
          <Sidebar
            URL={URL}
            loadAllCalculations={loadAllCalculations}
            calculations={calculations}
            loadOneCalculation={loadOneCalculation}
            calculate={calculate}
            setInputs={setInputs}
            deleteCalculations={deleteCalculations}
            createNewCalculation={createNewCalculation}
            isLoading={isLoadingLoadAll}
            handleSidebarDisplay={handleSidebarDisplay}
            displaySidebar={displaySidebar}
            setDisplaySidebar={setDisplaySidebar}
          />
        ) : (
          <MenuOpenIcon
            color="primary"
            fontSize="large"
            className="sidebar-open-icon"
            alt="side bar menu icon"
            onClick={handleSidebarDisplay}
          />
        )}
        <DecisionModal
          URL={URL}
          data={calculationInput}
          decisionModalIsOpen={decisionModalIsOpen}
          setDecisionModalIsOpenToFalse={setDecisionModalIsOpenToFalse}
          decision={decision}
          setDecision={setDecision}
          checkCalculationNameforSavingAs={checkCalculationNameforSavingAs}
          name={name}
          setName={setName}
          errorMessage={errorMessage}
          update={update}
          saveButtonDisabled={saveButtonDisabled}
        />
        <PricingInfoModal open={showPdfWarningModal} setOpen={setShowPdfWarningModal} role={user.role} />
        <div className="right-calculation">
          <section className="input loadingRelative">
            <h1>Liquefaction</h1>
            {isLoadingLoad && (
              <div className="loadingAbsolute">
                <CircularProgress size={30} />
              </div>
            )}
            <form>
              <TextField
                sx={{ width: 140 }}
                size="small"
                label="Name"
                variant="outlined"
                inputProps={{
                  maxLength: `${MAX_NAME_LENGTH}`,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={`Your calculation name (Max. ${MAX_NAME_LENGTH} characters)`}
                        arrow
                        placement="bottom"
                        TransitionComponent={Zoom}
                      >
                        <InfoTwoToneIcon fontSize={'string'} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                value={name}
                onChange={handleChangeName}
                disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
              />
              <TextField
                sx={{ width: 140 }}
                size="small"
                label={
                  <span key={'gamma'}>
                    &gamma; (kN/m<sup>3</sup> )
                  </span>
                }
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Natural unit weight of soil" arrow placement="bottom" TransitionComponent={Zoom}>
                        <InfoTwoToneIcon fontSize={'string'} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                value={gamma}
                onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Gamma', setGamma)}
                disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
              />
              <TextField
                sx={{ width: 140 }}
                size="small"
                label={'GWT (m)'}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title="Ground water level measured from ground surface"
                        arrow
                        placement="bottom"
                        TransitionComponent={Zoom}
                      >
                        <InfoTwoToneIcon fontSize={'string'} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                value={gwt}
                onChange={(e) => onChangeWarningForPositiveDecimals(e, 'GWT', setGwt)}
                disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
              />
              <TextField
                sx={{ width: 140 }}
                label={'Hammer Type'}
                size="small"
                select
                value={hammerType}
                onChange={(e) => setHammerType(e.target.value)}
                disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
              >
                <MenuItem value="0" disabled>
                  Select Hammer Type
                </MenuItem>
                <MenuItem value="1">Safety</MenuItem>
                <MenuItem value="2">Donut</MenuItem>
                <MenuItem value="3">Automatic</MenuItem>
              </TextField>
              <TextField
                sx={{ width: 140 }}
                label={'Sampler Type'}
                size="small"
                select
                value={samplerType}
                onChange={(e) => setSamplerType(e.target.value)}
                disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
              >
                <MenuItem value="0" disabled>
                  Select Sampler Type
                </MenuItem>
                <MenuItem value="1">Standard Sampler</MenuItem>
                <MenuItem value="2">Sampler Without Liner</MenuItem>
              </TextField>
              <TextField
                sx={{ minWidth: 140, maxWidth: 140 }}
                label={'Borehole Diameter'}
                size="small"
                select
                value={boreholeDiameter}
                onChange={(e) => setBoreholeDiameter(e.target.value)}
                disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
              >
                <MenuItem value="0" disabled>
                  Select Borehole Diameter
                </MenuItem>
                <MenuItem value="1">65-115</MenuItem>
                <MenuItem value="2">150</MenuItem>
                <MenuItem value="3">200</MenuItem>
              </TextField>
              <TextField
                sx={{ width: 140 }}
                size="small"
                label={
                  <span key={'mw'}>
                    M<sub>w</sub>
                  </span>
                }
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Magnitude" arrow placement="bottom" TransitionComponent={Zoom}>
                        <InfoTwoToneIcon fontSize={'string'} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                value={mw}
                onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Mw', setMw)}
                disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
              />
              <TextField
                sx={{ width: 140 }}
                size="small"
                label={
                  <span key={'sds'}>
                    S<sub>ds</sub>
                  </span>
                }
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title="Design spectral response acceleration parameter at short periods"
                        arrow
                        placement="bottom"
                        TransitionComponent={Zoom}
                      >
                        <InfoTwoToneIcon fontSize={'string'} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                value={sds}
                onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Sds', setSds)}
                disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
              />
              <TextField
                sx={{ width: 140 }}
                size="small"
                label={
                  <span key={'cm'}>
                    C<sub>m</sub>
                  </span>
                }
                variant="outlined"
                className="layeredDisabledInput"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Earthquake magnitude correction factor" arrow placement="bottom" TransitionComponent={Zoom}>
                        <InfoTwoToneIcon fontSize={'string'} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                value={round(result?.outputs?.cm, 2)}
                readOnly
                disabled
              />
            </form>
          </section>

          <section className="output loadingRelative horizontalScroll liqOutputSection">
            {isLoadingCalculate && (
              <div className="loadingAbsolute">
                <CircularProgress />
              </div>
            )}
            <div className="liqOutputHeader liqRowWidth">
              <Button
                variant="contained"
                startIcon={!isExporting ? <PictureAsPdfIcon /> : null}
                onClick={handleExportPdf}
                disabled={!showOutput || isExporting}
                style={{ width: '220px' }}
              >
                {isExporting ? (
                  <>
                    <CircularProgress size={18} style={{ marginRight: 8 }} />
                    Creating PDF…
                  </>
                ) : (
                  'Create PDF'
                )}
              </Button>
              <h2>Output</h2>
              <div className="liqOutputSpacer" />
            </div>
            <form className="liqForm">
              {layersFront.length > 0 && <LabelContainer />}
              {layersFront?.map((el, index) => {
                const layerKey = el.id ?? index;
                return (
                  <OutputLayer
                    key={layerKey}
                    layer={el}
                    index={index}
                    setLayers={setLayersFront}
                    resultsObj={resultProps[index]}
                    resultConst={result}
                    isLoadingCalculate={isLoadingCalculate}
                    isLoadingLoad={isLoadingLoad}
                    isLoadingSave={isLoadingSave}
                    isExpanded={expandedLayerId === layerKey}
                    onToggle={() => setExpandedLayerId((current) => (current === layerKey ? null : layerKey))}
                    canUserSeeCalculationSteps={Boolean(result?.outputs?.canUserSeeCalculationSteps)}
                  />
                );
              })}
            </form>
          </section>
          <div className="liqRowWidth">
            <ButtonContainer
              URL={URL}
              addLayer={addLayer}
              removeLayer={removeLayer}
              calculate={calculate}
              checkCalculationNameforSaving={checkCalculationNameforSaving}
              calculationInput={calculationInput}
              layersFrontLength={layersFront.length}
              isLoadingCalculate={isLoadingCalculate}
              isLoadingLoad={isLoadingLoad}
              isLoadingSave={isLoadingSave}
              saveButtonDisabled={saveButtonDisabled}
              calculateButtonDisabled={calculateButtonDisabled}
              calculationCountReached={calculationCountReached}
              saveCountReached={saveCountReached}
            />
          </div>
          {isNonliqLayerExists && (
            <div>
              Note*: If N<sub>1,60 f</sub> &nbsp;&ge; &nbsp;30, soils are considered too dense to liquefy and are classed as
              non-liquefiable. (Youd et al., 2001)
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

const LiquefactionWithProvider = () => {
  return (
    <CalculationContextProvider>
      <Liquefaction />
    </CalculationContextProvider>
  );
};
export default LiquefactionWithProvider;
